// Confirm a purchase on the New purchases card, then send its split request (when "Split this
// bill" is on), behind one tap. The purchase is filed first; a refused split (rate limit, no
// active link) leaves it filed in full and the answer says why. Pure: the two calls come in, the
// toast's tail comes out. The request itself is POST /api/splits, the same as the edit sheet's.
import { SPLIT_NOT_SENT, SPLIT_RESENT, SPLIT_SENT, validSplitPercent } from '../../shared/splits';

/**
 * Runs `confirm`; when it fails nothing else happens (it throws). Then `split`, if any: its
 * refusal never undoes the confirm. Returns what to add to the toast ('' without a split).
 */
export async function confirmThenSplit(
  confirm: () => Promise<unknown>,
  split: (() => Promise<unknown>) | null,
  partner: string,
  reason: (e: unknown) => string,
): Promise<string> {
  await confirm();
  if (!split) return '';
  try {
    await split();
    return ` ${SPLIT_SENT(partner)}`;
  } catch (e) {
    return ` ${SPLIT_NOT_SENT('Filed', reason(e))}`;
  }
}

/** A row's share, checked before anything is saved: whole percent 0 ("All Tiffany's") to 99. */
export const validPercent = (pct: number) => validSplitPercent(pct);

/** What a PATCH says about a waiting split it changed (src/routes/transactions.ts). */
export interface SplitAfterEdit {
  splitCancelled?: string;
  /** A new total cancelled it: send it again with this percent and the original request's note. */
  splitResend?: { myPercent: number; note: string | null };
}

/**
 * After an edit (the edit sheet's Save, a New purchases row's Confirm): a waiting split that a new
 * total cancelled goes out again with its original note, never the purchase's current one ('' when
 * it had none). The server checked first that it can (or refused the edit with the reason), so a
 * refusal here is a race; the toast says so. Returns the toast text, '' when no split changed.
 */
export async function resendAfterEdit(
  r: SplitAfterEdit,
  send: (myPercent: number, note: string) => Promise<unknown>,
  partner: string,
  reason: (e: unknown) => string,
  done: 'Saved' | 'Filed',
): Promise<string> {
  if (!r.splitResend) return r.splitCancelled ?? '';
  try {
    await send(r.splitResend.myPercent, r.splitResend.note ?? '');
    return SPLIT_RESENT(partner);
  } catch (e) {
    return SPLIT_NOT_SENT(done, reason(e));
  }
}
